// Feb 18, 2026 — Live pricing values pulled from /api/public/pricing.
//
// Replaces hardcoded strings like "$10/mo" and "$300" throughout the
// site so that when an admin moves a PricingLab slider, every help
// text, FAQ entry, and marketing line updates in lockstep.
//
// Cached for the session (TTL 5 min).
//
// Oct 3 2026 (Randy, stale-pricing cleanup) — the two PRICE fields used to
// fall back to `1000`, which is why "$10/mo" was still appearing site-wide
// three months after the price became $15: a slow or failed /public/pricing
// call silently rendered a retired price everywhere at once. Prices now fall
// back to `null` and `fmt$(null)` renders "—", so an unknown price LOOKS
// unknown instead of looking like $10. Structural values (caps, percentages)
// keep real defaults — they are layout, not money.
//
// Oct 3 2026, SECOND PASS (Randy: "verify the remaining user-facing pricing
// surfaces derive from the live pricing source rather than stale values") —
// the first pass only nulled the two platform-fee fields and left the rest
// remembering numbers. Two of those had ALREADY DRIFTED by the time they were
// checked, which is the proof that this rule has to cover every money field
// and not just the one that embarrassed us:
//
//     day_one_bundle_discount_threshold_cents   remembered 30000  ($300)
//                                               LIVE       33300  ($333)
//     affiliate_performance_rev_share_pct       remembered 30
//                                               LIVE       33
//
// So EVERY money and percentage value is now `null` here, and `fmt$`/`fmtPct`
// render "—". Only STRUCTURAL values (silo caps — limits, not money) keep a
// real default, because a missing cap changes layout rather than telling a
// customer the wrong price.
//
// THE SOURCES OF TRUTH, none of which live in the frontend:
//   platform_fee_cents / fan_cut_bps  → platform_settings.platform_fee_settings
//   affiliate_* / dfy_*               → platform_settings(id=global).thresholds
//   day_one_bundle_..._cents          → the Creator Pro catalog's
//                                       bundle.day1_cents, via _get_gate_cents()
// All three are aggregated by GET /api/public/pricing and edited in Super
// Admin → PricingLab. Never hardcode any of them in a component.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const FALLBACK = {
  // MONEY + PERCENTAGES — null on purpose. An unknown price must look unknown.
  platform_fee_cents: null,
  creator_monthly_fee_cents: null,
  fan_cut_bps: null,
  day_one_bundle_discount_threshold_cents: null,
  affiliate_base_rev_share_pct: null,
  affiliate_performance_rev_share_pct: null,
  affiliate_performance_active_customers_threshold: null,
  dfy_price_cents: null,
  dfy_operator_share_pct: null,
  // STRUCTURAL — limits and layout, not money. Real defaults are correct here.
  creator_tier_silo_cap: 3,
  creator_pro_tier_silo_cap: 12,
};

let _cache = null;
let _cachedAt = 0;
const TTL_MS = 5 * 60 * 1000;

export const fmt$ = (cents) => {
  if (cents === null || cents === undefined) return "—";
  const dollars = (cents || 0) / 100;
  return `$${dollars.toFixed(dollars % 1 === 0 ? 0 : 2)}`;
};

// Same contract as fmt$ for the percentage fields: never invent a rate.
export const fmtPct = (pct) =>
  (pct === null || pct === undefined ? "—" : `${pct}%`);

export function usePublicPricing() {
  const [data, setData] = useState(() => (_cache && Date.now() - _cachedAt < TTL_MS ? _cache : FALLBACK));
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache && Date.now() - _cachedAt < TTL_MS) return;
    let cancel = false;
    api.get("/public/pricing")
      .then((r) => {
        if (cancel) return;
        _cache = r.data;
        _cachedAt = Date.now();
        setData(r.data);
      })
      .catch(() => { /* stay on fallback */ })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  return { ...data, loading };
}
